import { useRef, useState } from "react";
import { FileSpreadsheet, Upload, Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ACTION = { create: ["Nuovo", "text-emerald-700"], update: ["Aggiornato", "text-sky-700"], skip: ["Scartato", "text-red-700"] };

export function ImportDialog({ open, onOpenChange, onDone, title, description, templateUrl, exportUrl, importFn, entityLabel = "righe", testPrefix = "import" }) {
  const inp = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setFile(null); setPreview(null); if (inp.current) inp.current.value = ""; };
  const pick = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true); setPreview(null);
    try { setPreview(await importFn(f, true)); }
    catch (e) { toast.error(e?.response?.data?.detail || "File non valido"); reset(); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await importFn(file, false);
      toast.success(`Import completato: ${r.created} nuovi, ${r.updated} aggiornati, ${r.skipped} scartati`);
      onDone?.(); onOpenChange(false); reset();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import non riuscito"); }
    finally { setBusy(false); }
  };
  const ok = preview ? preview.created + preview.updated : 0;
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" /> {title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" asChild data-testid={`${testPrefix}-template`}><a href={templateUrl} download><Download className="h-3.5 w-3.5" /> Scarica modello Excel</a></Button>
          {exportUrl && <Button variant="outline" size="sm" className="gap-1.5" asChild data-testid={`${testPrefix}-export`}><a href={exportUrl} download><Download className="h-3.5 w-3.5" /> Esporta {entityLabel} attuali</a></Button>}
          <input ref={inp} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid={`${testPrefix}-file`} />
          <Button size="sm" className="gap-1.5" disabled={busy} onClick={() => inp.current?.click()} data-testid={`${testPrefix}-pick`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {file ? "Cambia file" : "Scegli file da importare"}</Button>
        </div>
        {preview && (
          <div className="space-y-2" data-testid={`${testPrefix}-preview`}>
            <p className="text-sm"><b>{file?.name}</b> · anteprima: <span className="text-emerald-700 font-semibold" data-testid={`${testPrefix}-create-count`}>{preview.created} nuovi</span> · <span className="text-sky-700 font-semibold">{preview.updated} da aggiornare</span> · <span className="text-red-700 font-semibold" data-testid={`${testPrefix}-skip-count`}>{preview.skipped} scartati</span></p>
            <div className="max-h-64 overflow-auto rounded-md border border-border">
              <table className="w-full text-xs"><thead className="bg-secondary/60 sticky top-0"><tr className="text-left"><th className="p-1.5">Riga</th><th className="p-1.5">Nome</th><th className="p-1.5">Esito</th><th className="p-1.5">Nota</th></tr></thead>
                <tbody>{preview.rows.map((r) => <tr key={r.row} className="border-t border-border/60"><td className="p-1.5 text-muted-foreground">{r.row}</td><td className="p-1.5 font-semibold">{r.name || "—"}</td><td className={`p-1.5 font-semibold ${ACTION[r.action][1]}`}>{ACTION[r.action][0]}</td><td className="p-1.5 text-muted-foreground">{r.error || ""}</td></tr>)}</tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { onOpenChange(false); reset(); }}>Annulla</Button>
              <Button disabled={busy || !ok} onClick={confirm} data-testid={`${testPrefix}-confirm`}>{busy ? "Importazione…" : `Importa ${ok} ${entityLabel}`}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
